import React from "react";
import Select from "react-select";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import {
    VAULT77_HEALTH_DEFAULT_COLOR,
    VAULT77_HEALTH_ISSUE_COLORS,
    VAULT77_HEALTH_ISSUE_LABELS,
    VAULT77_HEALTH_SUMMARY_ORDER,
    type Vault77HealthReport,
} from "../types";
import "./HealthHeader.css";

interface EmployeeOption {
    value: number;
    label: string;
}

export interface HealthHeaderProps {
    report: Vault77HealthReport | null;
    loading: boolean;
    error: string | null;
    onRefresh: () => void;
    /** Called with an employee id (== HR UserId) when one is picked from the dropdown. */
    onSelectEmployee: (employeeId: number) => void;
    /**
     * The issue code currently filtering the employee list, or null when no gauge filter is active.
     * Drives the active-gauge highlight and scopes the jump-to dropdown to matching employees.
     */
    activeIssueCode?: string | null;
    /** Called with an issue code when a gauge is clicked (the parent toggles list filtering). */
    onSelectIssueType?: (code: string) => void;
}

// Render the menu in a body portal so it isn't clipped by the Employees page's
// `overflow: hidden` / fixed-height container, and keep it above the table z-index.
const menuPortalTarget = typeof document !== "undefined" ? document.body : undefined;
const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    control: (base: any) => ({ ...base, minHeight: 34, height: 34 }),
    container: (base: any) => ({ ...base, width: 230 }),
    valueContainer: (base: any) => ({ ...base, padding: "0 6px" }),
    indicatorsContainer: (base: any) => ({ ...base, height: 34 }),
};

// Compact center label for a gauge (counts can be 4 digits).
function abbreviate(n: number): string {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

/**
 * Compact Vault77 health control for the Employees toolbar (next to the search filters): a row of
 * color-coded circular gauges (one per issue type, sized to fit the toolbar height) plus a
 * react-select dropdown of the unhealthy employees. Picking one fires `onSelectEmployee` (the parent
 * scrolls to that row and opens the record). Each gauge's ring fills to the % of scope affected and
 * shows the count in the center; hover for the label/exact figures.
 */
const HealthHeader: React.FC<HealthHeaderProps> = ({
    report,
    loading,
    error,
    onRefresh,
    onSelectEmployee,
    activeIssueCode = null,
    onSelectIssueType,
}) => {
    if (loading && !report) {
        return (
            <output className="Vault77HealthHeader Vault77HealthHeader--loading" title="Checking employee records">
                <span className="material-icons spin">autorenew</span>
                <span className="Vault77HealthHeader__text">Checking…</span>
            </output>
        );
    }

    if (error && !report) {
        return (
            <div className="Vault77HealthHeader Vault77HealthHeader--error" role="alert" title={error}>
                <span className="material-icons">error_outline</span>
                <span className="Vault77HealthHeader__text">Health unavailable</span>
                <button type="button" className="Vault77HealthHeader__refresh" onClick={onRefresh} title="Retry">
                    <span className="material-icons">refresh</span>
                </button>
            </div>
        );
    }

    if (!report) return null;

    if (report.EmployeesWithIssues === 0) {
        return (
            <output
                className="Vault77HealthHeader Vault77HealthHeader--healthy"
                title={`All ${report.EmployeesInScope} employee records look healthy`}
            >
                <span className="material-icons">verified_user</span>
                <span className="Vault77HealthHeader__text">Records healthy</span>
                <button type="button" className="Vault77HealthHeader__refresh" onClick={onRefresh} title="Re-check">
                    <span className="material-icons">refresh</span>
                </button>
            </output>
        );
    }

    const counts = report.Counts;
    const scope = report.EmployeesInScope || 0;
    const summary = VAULT77_HEALTH_SUMMARY_ORDER.map((s) => ({
        code: s.code,
        label: VAULT77_HEALTH_ISSUE_LABELS[s.code] || s.code,
        value: s.get(counts),
    })).filter((s) => s.value > 0);

    // When a gauge filter is active, scope the jump-to dropdown to employees with that issue type.
    const dropdownEmployees = activeIssueCode
        ? report.Employees.filter((emp) => emp.Issues.some((i) => i.Code === activeIssueCode))
        : report.Employees;
    const options: EmployeeOption[] = dropdownEmployees.map((emp) => ({
        value: emp.EmployeeId,
        label: `${emp.EmployeeName || `Employee ${emp.EmployeeId}`} (${emp.Issues.length})`,
    }));

    return (
        <div className="Vault77HealthHeader Vault77HealthHeader--issues">
            <span
                className="material-icons Vault77HealthHeader__warn"
                title={`${report.EmployeesWithIssues} employees need attention`}
            >
                report_problem
            </span>

            <span className="Vault77HealthHeader__gauges">
                {summary.map((s) => {
                    const color = VAULT77_HEALTH_ISSUE_COLORS[s.code] || VAULT77_HEALTH_DEFAULT_COLOR;
                    const pct = scope > 0 ? Math.min(100, Math.round((s.value / scope) * 100)) : 0;
                    const isActive = activeIssueCode === s.code;
                    const clickable = typeof onSelectIssueType === "function";
                    let hint = "";
                    if (clickable) hint = isActive ? " — click to clear filter" : " — click to filter the list";
                    return (
                        <button
                            type="button"
                            key={s.code}
                            className={`Vault77HealthHeader__gauge${isActive ? " Vault77HealthHeader__gauge--active" : ""}`}
                            style={isActive ? { borderBottomColor: color } : undefined}
                            disabled={!clickable}
                            aria-pressed={isActive}
                            onClick={() => onSelectIssueType?.(s.code)}
                            title={`${s.label}: ${s.value}${scope > 0 ? ` (${pct}% of ${scope})` : ""}${hint}`}
                        >
                            <CircularProgressbar
                                value={pct}
                                text={abbreviate(s.value)}
                                strokeWidth={10}
                                styles={buildStyles({
                                    pathColor: color,
                                    textColor: color,
                                    trailColor: "#eef0f2",
                                    textSize: "34px",
                                    strokeLinecap: "round",
                                })}
                            />
                        </button>
                    );
                })}
            </span>

            <Select
                className="Vault77HealthHeader__select"
                classNamePrefix="vault77-health-select"
                options={options}
                value={null}
                onChange={(opt: any) => {
                    if (opt) onSelectEmployee(opt.value as number);
                }}
                placeholder={`Jump to employee (${options.length})`}
                isSearchable
                menuPortalTarget={menuPortalTarget}
                styles={selectStyles}
            />

            <button type="button" className="Vault77HealthHeader__refresh" onClick={onRefresh} title="Re-check">
                <span className="material-icons">refresh</span>
            </button>
        </div>
    );
};

export default HealthHeader;
